import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Btn, FORMATIONS, Kicker, SectionHead, DevisCta, BadgeAC } from "@/components/Shared";
import { Stethoscope, PencilRuler, UsersRound, ClipboardCheck, ArrowRight, BookOpen, Sparkles, UtensilsCrossed } from "lucide-react";

const PROOFS = [
  "25 ans de terrain",
  "Titre FPA",
  "Master 2 (niv. 7)",
  "2 ouvrages publiés",
  "Dossier DRAAF déposé",
];

const METHOD = [
  {
    icon: Stethoscope,
    titre: "Diagnostic",
    texte:
      "Échange approfondi sur votre activité, vos équipes et vos contraintes. Le besoin réel avant le besoin exprimé.",
  },
  {
    icon: PencilRuler,
    titre: "Conception sur mesure",
    texte:
      "Un programme construit pour votre contexte : vos outils, vos procédures, vos cas réels. Rien de générique.",
  },
  {
    icon: UsersRound,
    titre: "Animation active",
    texte:
      "Ateliers, mises en situation, manipulation directe. On apprend en faisant, pas en écoutant.",
  },
  {
    icon: ClipboardCheck,
    titre: "Évaluation et suivi",
    texte:
      "Évaluation des acquis, supports remis, suivi post-formation pour ancrer les pratiques.",
  },
];

const FormationIcons = { 0: Sparkles, 1: PencilRuler, 2: UtensilsCrossed };

export default function Home() {
  return (
    <div data-testid="page-accueil">
      <Seo
        title="Form Action Pro — Formation professionnelle, IA, no-code & hygiène alimentaire"
        description="Des équipes formées, des pratiques conformes, des procédures que l'on applique. Formation IA & no-code, hygiène et Plan de Maîtrise Sanitaire, audit d'établissement — par un formateur certifié issu du métier."
        path="/"
      />

      <section className="relative min-h-[92vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&q=80"
            alt="Équipe en formation professionnelle autour d'une table de travail"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-marine/80 via-marine/75 to-marine/85" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto py-24">
          <span
            data-testid="hero-badge"
            className="inline-block bg-vert/10 border border-vert text-vert font-sans font-semibold text-[13px] px-4 py-2 rounded-full mb-6 tracking-wide animate-fade-up"
          >
            Organisme de formation — Golfe de Saint-Tropez, toute la France
          </span>
          <h1
            className="font-serif font-semibold text-white text-4xl md:text-5xl lg:text-[58px] leading-[1.1] mb-6 animate-fade-up"
            style={{ animationDelay: "0.1s" }}
          >
            Des équipes formées, des pratiques conformes, des procédures que l’on applique.
          </h1>
          <p
            className="font-sans text-lg md:text-xl text-white/85 max-w-2xl mx-auto mb-10 animate-fade-up"
            style={{ animationDelay: "0.2s" }}
          >
            Formation à l’IA et au no-code, accompagnement hygiène et Plan de Maîtrise Sanitaire,
            audit d’établissement — par un formateur certifié issu du métier.
          </p>
          <div
            className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up"
            style={{ animationDelay: "0.3s" }}
          >
            <Btn to="/contact" testid="hero-cta-devis" variant="primary">
              Demander un devis
            </Btn>
            <Btn to="/livres" testid="hero-cta-livres" variant="outlineLight">
              Découvrir mes livres
            </Btn>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden bg-black/30 py-3">
          <div className="flex whitespace-nowrap animate-marquee">
            {[0, 1].map((n) => (
              <span key={n} className="font-sans text-[13px] text-white/70 mr-0">
                {`  ${PROOFS.join("  ·  ")}  ·`.repeat(3)}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sable py-14 border-b border-bordure">
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap justify-center gap-3">
          {PROOFS.map((p) => (
            <span
              key={p}
              data-testid="proof-chip"
              className="font-sans text-sm font-medium text-marine bg-white border border-bordure rounded-full px-5 py-2.5 shadow-[0_1px_8px_rgba(0,0,0,0.04)]"
            >
              {p}
            </span>
          ))}
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHead kicker="Nos formations" title={<>Trois expertises,<br />une exigence : le terrain.</>} />
          <div className="grid md:grid-cols-3 gap-6">
            {FORMATIONS.map((f, i) => {
              const Icon = FormationIcons[i];
              return (
                <div
                  key={f.to}
                  data-testid={`card-${f.to.split("/").pop()}`}
                  className="bg-white border border-bordure hover:border-vert rounded-lg p-8 shadow-[0_2px_20px_rgba(0,0,0,0.06)] transition-colors duration-300 flex flex-col gap-4"
                >
                  <Icon className="text-vert-dark" size={30} />
                  <h3 className="font-serif text-2xl text-marine">{f.label}</h3>
                  <span className="font-sans font-semibold text-xs px-3 py-1 rounded-full w-fit bg-vert/10 text-vert-dark">
                    {f.duree}
                  </span>
                  <p className="font-sans text-sm text-gris-texte leading-relaxed flex-1">{f.texte}</p>
                  <Link
                    to={f.to}
                    data-testid={`card-link-${f.to.split("/").pop()}`}
                    className="font-sans font-semibold text-sm text-vert-dark hover:underline inline-flex items-center gap-1.5"
                  >
                    Découvrir la formation <ArrowRight size={15} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-sable py-20">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHead kicker="La méthode" title={<>Une méthode éprouvée,<br />du diagnostic au suivi.</>} />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            {METHOD.map((m, i) => (
              <div key={m.titre} data-testid={`methode-etape-${i + 1}`} className="relative">
                <p className="font-serif text-[64px] text-vert/25 leading-none mb-2">0{i + 1}</p>
                <m.icon className="text-vert-dark mb-3" size={28} />
                <h3 className="font-serif text-2xl text-marine mb-3">{m.titre}</h3>
                <p className="font-sans text-[15px] text-gris-texte leading-relaxed">{m.texte}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Kicker>Livres &amp; ressources</Kicker>
              <h2 className="font-serif text-4xl md:text-[42px] text-marine leading-tight mb-6">
                La collection<br />« Le Monde en fiches ».
              </h2>
              <p className="font-sans text-base text-marine/80 leading-[1.7] mb-4">
                Des ouvrages de référence clairs et structurés, nés du même souci : rendre simple ce
                qui ne l’est pas. <em>L’IA sans jargon</em> et l’<em>Atlas économique mondial</em>,
                disponibles en PDF.
              </p>
              <blockquote className="my-8 border-l-[3px] border-vert pl-6">
                <p className="font-serif italic text-lg text-vert-dark">
                  « Un outil ne vaut que par la clarté de son mode d’emploi. »
                </p>
              </blockquote>
              <Btn to="/livres" testid="home-cta-livres" variant="dark">
                Voir les livres
              </Btn>
            </div>
            <div className="grid gap-6">
              <div className="bg-sable border border-bordure rounded-lg p-8 flex items-start gap-5">
                <BookOpen className="text-vert-dark shrink-0 mt-1" size={28} />
                <div>
                  <h3 className="font-serif text-xl text-marine mb-1">L’IA sans jargon</h3>
                  <p className="font-sans text-sm text-gris-texte leading-relaxed mb-3">
                    110 fiches · 155 pages · glossaire de 177 termes · sections RGPD et AI Act.
                  </p>
                  <p className="font-sans text-sm font-semibold text-marine">10 € TTC — édition PDF</p>
                </div>
              </div>
              <div className="bg-sable border border-bordure rounded-lg p-8 flex items-start gap-5">
                <BookOpen className="text-vert-dark shrink-0 mt-1" size={28} />
                <div>
                  <h3 className="font-serif text-xl text-marine mb-1">Atlas économique mondial</h3>
                  <p className="font-sans text-sm text-gris-texte leading-relaxed mb-3">
                    193 pays · 209 pages couleur · chiffres officiels datés.
                  </p>
                  <p className="font-sans text-sm font-semibold text-marine">10 € TTC — édition PDF</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-marine py-20">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Kicker dark>Ressource offerte</Kicker>
            <h2 className="font-serif text-4xl text-white leading-tight mb-6">
              Le petit dico de l’IA — offert.
            </h2>
            <p className="font-sans text-base text-white/75 leading-[1.7] mb-8">
              177 termes d’IA expliqués simplement, avec les équivalents français officiels du
              Journal officiel et de FranceTerme. 25 pages à garder sous la main.
            </p>
            <Btn to="/dico-ia" testid="home-cta-dico" variant="primary">
              Recevoir le dico gratuitement
            </Btn>
          </div>
          <div className="bg-marine-light border border-vert/25 rounded-lg p-8">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="font-serif text-[42px] text-vert leading-none mb-2">177</p>
                <p className="font-sans text-xs text-white/60">termes définis</p>
              </div>
              <div>
                <p className="font-serif text-[42px] text-vert leading-none mb-2">25</p>
                <p className="font-sans text-xs text-white/60">pages</p>
              </div>
              <div>
                <p className="font-serif text-[42px] text-vert leading-none mb-2">0 €</p>
                <p className="font-sans text-xs text-white/60">offert</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-sable py-20">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Kicker>Le projet Tablecho</Kicker>
            <h2 className="font-serif text-4xl text-marine leading-tight mb-6">
              Une application en construction<br />pour la restauration.
            </h2>
            <p className="font-sans text-base text-marine/80 leading-[1.7] mb-8">
              Tablecho est un projet d’application destiné aux professionnels de la restauration,
              porté par François Schoonheere. En cours de construction — présentation honnête,
              sans promesse excessive.
            </p>
            <Btn to="/tablecho" testid="home-cta-tablecho" variant="outlineDark">
              Découvrir Tablecho
            </Btn>
          </div>
          <div className="rounded-lg overflow-hidden shadow-[0_2px_20px_rgba(0,0,0,0.08)]">
            <img
              src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=900&q=80"
              alt="Salle de restaurant élégante"
              className="w-full h-[320px] object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <DevisCta />
    </div>
  );
}
